import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import type {
  ComponentContent,
  InterventionPreview,
  Question,
  QuestionnaireType,
  Score,
} from '@/ts/types/dto/interventions-dto';
import type {JournalEntryDTO} from '@/ts/types/dto/journals-dto';
import type {LibraryChallengeDTO} from '@/ts/types/dto/recco/library.dto';

/**
 * Mirror of the `GET /api/v1/me/today` payload (BE models under `services/models/today/`,
 * branch `feature/IPD-2968-today-page-payload`, PR #773). Types only: each tile's mapper
 * derives its view-model from these on its own ticket's branch.
 *
 * Contract points, ANSWERED on BE PR #773 (2026-08-17) — kept here because each one shapes a
 * mapper decision:
 * - `TodaySessionDTO.openPageId` names the page the session CTA should open: the first not
 *   completed, or the session's first when all are. The choice lives in the BE on purpose, written
 *   once next to the page order. Null only for broken CMS content (a session with no pages).
 *   The FE cannot route to it, though: the app's session route takes a 1-BASED POSITION in `pid`,
 *   not a page id, so an id alone cannot express a resume point. Hence `openPageNumber` below,
 *   asked of the backend on 2026-08-20 and shipped on BE PR #778.
 * - `UpcomingTileDTO.kind` stays nullable FOR GOOD: a license with empty recommendation packs
 *   produces no task, no next unlock and no recommendation, and the resolver falls through to an
 *   empty tile. The null branch in the upcoming mapper is required behavior, not defence.
 * - EXPLORE_SECTION: an org that blacklists Explore is expected to blacklist Home too, and the
 *   page already redirects on HOME_SECTION before rendering — but that is how orgs are configured
 *   today, not something the frontend is given. `useFeatureAccess` reads the two flags
 *   independently, so the combination is reachable, and the discover row is the one band whose
 *   every tile leads into Explore. IPD-2978 therefore drops those three tiles (and with them the
 *   band) when Explore is blacklisted, with tests both ways. Superseded note: an earlier version
 *   of this list said "no conditional logic anywhere", which read as licence to delete that gate.
 * - `ContentTileDTO.items` normally carries two pieces but can carry ONE — when a pinned piece
 *   disappears from Directus mid-window, the survivor stays alone for the rest of the window —
 *   or NONE: an empty recommendation pool yields an empty list (`getContentTile` ends in
 *   `ifEmpty { pool }.take(SLOTS)`, and both can be empty; observed on a real payload). Never
 *   assume any length; a missing piece drops its cell rather than rendering empty.
 * - The assessment slider ships the whole list when the onboarding one is open (the prototype
 *   draws that state both as a single card and as a carousel); the BE adds no flag until design
 *   says they are two states, so a trim — if design lands on the single card — belongs in the
 *   assessment mapper.
 */

/** DTO type for GetTodayResponse */
export type GetTodayResponse = TodayDTO;

/**
 * Mirror of the BE's `ComponentQuestionnaire` (services/models/ComponentQuestionnaire.kt), which
 * is what every questionnaire in this payload actually is. Deliberately NOT the existing
 * `Questionnaire` mirror: that one models the session-scope shape and promises `citations` this
 * payload never sends (in the me spec, citations exist only on indications and interventions) and
 * a non-null `description` — a mapper written against it compiles and throws at runtime.
 */
export interface ComponentQuestionnaireDTO {
  id: UuidDTO;
  name: string;
  description: string | null;
  image: UuidDTO | null;
  /**
   * An animated illustration the CMS holds for this questionnaire, as a Directus file id. Null
   * wherever it holds none, which is most of them: the picture above is what gets drawn then.
   */
  animation?: UuidDTO | null;
  /** The card colour the CMS holds for it, as a hex string. Null lets the tile choose one. */
  backgroundColor?: string | null;
  questions: Question[];
  score: Score | null;
  hasScore: boolean;
  type: QuestionnaireType;
  /** Hours before the questionnaire can be answered again; null means it never reopens. */
  cooldownHours: number | null;
  isAnswered: boolean;
  tags: string[];
  /** BE enum `ContentTopic`; mirrored as a string because nothing in the FE consumes it yet. */
  topic: string | null;
}

/**
 * The whole Today page in one call. Always send `?timezone=` (IANA name): the BE falls back to
 * the server's own zone, not the user's. The call has write effects (it fixes the content pair
 * for the window, and reading an expired challenge can mark it FAILED), so never prefetch it
 * or call it speculatively.
 */
export interface TodayDTO {
  userName: string;
  upcoming: UpcomingTileDTO;
  tasks: TasksTileDTO;
  content: ContentTileDTO;
  assessment: AssessmentTileDTO | null;
  program: ProgramTileDTO;
  journal: JournalTileDTO;
  checklist: ChecklistTileDTO | null;
  /**
   * When the reader last answered any assessment, null while they never have (IPD-3257).
   *
   * At the root and not on the assessment tile, because that tile goes: once every assessment they
   * could answer has been answered and none of them reopens, the backend sends no tile at all and a
   * date on it would go with it. The per-item `lastAnsweredAt` cannot stand in for this either —
   * the tile's carousel is what the recommendation engine offers now, and an assessment that leaves
   * it takes its date along.
   *
   * Optional only while a deployment that predates the field is still running: the tile falls back
   * to reading the carousel, which is right for every reader whose answers are still on it.
   */
  lastAssessmentAnsweredAt?: string | null;
}

/** Which of the upcoming tile's states applies, each with its own payload field */
export enum UpcomingTileKindDTO {
  ONBOARDING_CTA = 'ONBOARDING_CTA',
  TASK = 'TASK',
  NO_TASK_DUE = 'NO_TASK_DUE',
  RECOMMENDATION = 'RECOMMENDATION',
}

/** The hero tile: the one thing the page suggests doing next */
export interface UpcomingTileDTO {
  /** Null while none of the implemented cases applies to the user */
  kind: UpcomingTileKindDTO | null;
  /** The onboarding questionnaire, on ONBOARDING_CTA */
  questionnaire: ComponentQuestionnaireDTO | null;
  /** The task to do next, on TASK */
  task: TodayTaskDTO | null;
  /** When the next task becomes available (ISO-8601 instant), only on NO_TASK_DUE */
  nextTaskDueDate: string | null;
  /**
   * What that date belongs to, on NO_TASK_DUE. The backend cannot send the date without it — both
   * come off one `UpcomingWait` whose two fields are non-null — and when it arrives it arrives
   * whole, never partial. Null on the other three kinds, which is the only reason it is nullable:
   * one class serves all four.
   *
   * Present and not optional, because a release ships both sides together, so no deployment runs
   * this page against a backend that predates the field. What is left is the rollout itself and a
   * rollback, and the mapper answers a missing one with the idle face rather than a sentence about
   * a date it cannot finish — that guard is runtime and does not depend on this type.
   */
  nextTask: NextTaskDTO | null;
  /** The user answered the onboarding questionnaire earlier today, so the greeting can say so */
  justOnboarded: boolean;
  /** One of these three carries the suggestion on RECOMMENDATION */
  intervention: InterventionPreview | null;
  challenge: ChallengePreviewDTO | null;
  content: ComponentContent | null;
}

/** The session the unlock date belongs to, and where it sits in its programme or challenge */
export interface NextTaskDTO {
  /** The programme or challenge the session belongs to */
  name: string;
  /** The session's own title, the same one it carries anywhere else in the payload */
  sessionName: string;
  /** Position of the session within its programme or challenge, starting at 1 */
  sessionNumber: number;
  totalSessions: number;
}

/**
 * The recommended challenge on the upcoming tile. Same BE class (`ChallengePreview`) that the
 * Explore library items carry, so the existing mirror is reused rather than duplicated.
 */
export type ChallengePreviewDTO = LibraryChallengeDTO;

/** What kind of thing a task is, discriminating which sub-object is set */
export enum TodayTaskTypeDTO {
  SESSION = 'SESSION',
  HOMEWORK = 'HOMEWORK',
  CHALLENGE = 'CHALLENGE',
  ASSESSMENT = 'ASSESSMENT',
}

/**
 * One pending (or finished) item of the user's day. No id and no flat fields of its own:
 * `type` says which of the four sub-objects carries the data. The BE's `availableSince` is
 * `@JsonIgnore`d and never serialized — the list order already answers "what comes first".
 */
export interface TodayTaskDTO {
  type: TodayTaskTypeDTO;
  /**
   * Finished today, measured in the timezone this request carried. For an assessment that is
   * `TodayAssessmentService.getTasks` comparing the last answer's day to the caller's — so it says
   * "answered today", never "still on cooldown": one off its cooldown that the user answered this
   * morning is both, and this page reads it as a questionnaire with nothing left to answer.
   */
  done: boolean;
  session: TodaySessionDTO | null;
  homework: TodayHomeworkDTO | null;
  challenge: TodayChallengeDTO | null;
  questionnaire: ComponentQuestionnaireDTO | null;
}

/** An intervention session as a task */
export interface TodaySessionDTO {
  sessionId: UuidDTO;
  sessionName: string;
  /** Position of the session within its intervention, starting at 1 */
  sessionNumber: number;
  /** Value between 0 and 1: the session's completed pages over its total */
  sessionProgress: number;
  /**
   * The page the session CTA opens: the first the user has not completed, or the session's first
   * page when all are done. Null only when the session carries no pages in the CMS, which is
   * broken content rather than a user state — route to the session start in that case.
   */
  openPageId: UuidDTO | null;
  /**
   * The 1-based position of `openPageId` within the session's page order, which is what the app's
   * session route actually consumes (`pid` is a position, never an id), so it goes into the route
   * as it arrives. Same page as `openPageId` and derived from the same answer in the backend, so
   * the two cannot name different pages.
   *
   * `Number` rather than `Index` because that is what the backend calls a position starting at 1
   * (`sessionNumber`, just above); a position starting at 0 is an `Index` there, as in the home
   * payload's `currentPageIndex`, which `use-links.ts` adds 1 to. A 1-based `openPageIndex` one
   * letter away from a 0-based `currentPageIndex` was the off-by-one this field exists to close.
   *
   * Null only when the session carries no pages in the CMS — broken content, not a user state. It
   * and `openPageId` travel together: either both carry a page or both are null, never one alone.
   *
   * Optional as well as nullable ONLY because BE PR #778 is not deployed everywhere the page is
   * tested: against an older backend the field does not arrive at all and the mapper opens page 1.
   * The key is otherwise always present — a null one included — so the day every environment has
   * it, this becomes `number | null`.
   *
   * Not to be read off the spec's `required` list, which leaves it out: that is only how springdoc
   * renders a nullable Kotlin property, and it cannot express "always present, sometimes null".
   */
  openPageNumber?: number | null;
  interventionId: UuidDTO;
  interventionName: string;
  /** Directus asset id of the intervention's image */
  interventionImage: UuidDTO;
}

/** A homework as a task */
export interface TodayHomeworkDTO {
  /** The homework content. Its session has not been reached yet, so there is no user id */
  contentId: UuidDTO;
  /** Set once the user has started it */
  userHomeworkId: UuidDTO | null;
  /**
   * Homework carries no title of its own: this is the name of its page, which today reads
   * "Homework" in every intervention. The session name is the only descriptive text available.
   */
  title: string;
  sessionId: UuidDTO;
  sessionName: string;
  interventionId: UuidDTO;
  interventionName: string;
}

/** A challenge session as a task. Challenge ids are ints, unlike every other id here */
export interface TodayChallengeDTO {
  challengeId: number;
  challengeName: string;
  /** Absolute URL, not a Directus asset id */
  challengeImageUrl: string | null;
  sessionId: number;
  sessionName: string;
  /** Position of the session within its challenge, starting at 1 */
  sessionNumber: number;
  /** Value between 0 and 1: the completed tasks of this session over its total */
  sessionProgress: number;
}

/** Which of the tasks tile's states applies */
export enum TasksTileKindDTO {
  VIDEO = 'VIDEO',
  TASK_LIST = 'TASK_LIST',
  ALL_CAUGHT_UP = 'ALL_CAUGHT_UP',
  PROMO = 'PROMO',
}

/** What the promo state of the tasks tile advertises */
export enum PromoDTO {
  CHALLENGE = 'CHALLENGE',
  MEDICAL_PROGRAM = 'MEDICAL_PROGRAM',
  NON_MEDICAL_PROGRAM = 'NON_MEDICAL_PROGRAM',
}

/**
 * The overflow tile: the tasks that do not fit in the upcoming one, or what to show in their
 * absence. The list opens with the task the upcoming tile is already showing, so the user reads
 * it as the head of the queue rather than as a repetition.
 */
export interface TasksTileDTO {
  kind: TasksTileKindDTO;
  /** The welcome video, on VIDEO */
  video: ComponentContent | null;
  /**
   * Whether the video should start itself, on VIDEO. False once a presentation is on record
   * (IPD-3114). Absent on a deployment that predates the field, which is read as FALSE: without the
   * field there is nothing that can ever turn the autoplay off, so reading absence as consent would
   * start the video on every visit and report every one of them — the opposite of the once this is
   * for. See `use-today-tasks-mapper`.
   */
  autoplay?: boolean | null;
  /** Pending first, then finished-today; empty except on TASK_LIST */
  tasks: TodayTaskDTO[];
  promo: PromoDTO | null;
}

/** The pieces of reading the page offers, refreshed together every few days */
export interface ContentTileDTO {
  /** Two in the normal case, but can be ONE — see the header note. Never assume length */
  items: ComponentContent[];
  /**
   * When this pair was chosen for the reader (ISO-8601 instant), which is what dates the tile's
   * contents against the answers behind them (IPD-3256). Null when there is nothing to show.
   *
   * An instant rather than a day on purpose: the pair is pinned on the visit a new reader makes,
   * and their first assessment is usually answered in that same visit, so a day-level value would
   * have swallowed exactly the transition this is for.
   *
   * Optional only while the backend that sends it is not deployed everywhere the app runs; absent,
   * the tile stays in the state it has today.
   */
  pinnedAt?: string | null;
}

/** Whether an assessment can be answered right now or is waiting its turn */
export enum AssessmentStatusDTO {
  AVAILABLE = 'AVAILABLE',
  LOCKED = 'LOCKED',
}

/** The user's assessments, one open at a time and the rest waiting their turn */
export interface AssessmentTileDTO {
  items: TodayAssessmentDTO[];
}

/** One assessment of the tile's carousel */
export interface TodayAssessmentDTO {
  status: AssessmentStatusDTO;
  /**
   * When the reader last answered THIS assessment (ISO-8601 instant), null for one they never
   * answered (IPD-3256). Per assessment rather than one date on the payload, so the question "what
   * did the platform last learn about them" is the latest of these.
   *
   * It is also the honest answer to "has this reader ever answered anything", which neither
   * `questionnaire.isAnswered` (mapped from the CMS without their answers, so false for everyone)
   * nor `unlockDate` (gone once the cooldown expires) can give.
   *
   * Optional only while the backend that sends it is not deployed everywhere the app runs.
   */
  lastAnsweredAt?: string | null;
  /**
   * When it reopens (ISO-8601 instant), for one already answered. Absent while it is simply
   * queued behind another — and absent means either null or no field at all, since the contract
   * says nothing about which and the backend serialises it as an optional.
   */
  unlockDate?: string | null;
  questionnaire: ComponentQuestionnaireDTO;
}

/** The two groups the program tile files the catalogue into */
export enum InterventionTypeDTO {
  MEDICAL = 'MEDICAL',
  NON_MEDICAL = 'NON_MEDICAL',
}

/**
 * Where the user stands in the programs they are in, for the FE to group into Balm Medical and
 * non-medical. One nobody categorised in Directus is left out rather than guessed into a group.
 */
export interface ProgramTileDTO {
  /**
   * The programs the user is in, whether under way or finished. Not the catalogue their licence
   * grants: one they never joined has no progress to report, and one they withdrew from would
   * report the progress of an attempt they walked away from.
   */
  programs: TodayProgramDTO[];
  /**
   * The groups holding a program this user could START. Not the same as the groups their licence
   * covers: one whose only program they already finished is absent, as is one whose programs are
   * discontinued, in maintenance or still in preview. A group missing here has nothing to offer,
   * so inviting them to browse it leads nowhere.
   *
   * It answers what `programs` cannot: those are only the user's own, so a licence that offers
   * prevention to someone who joined none of it looks, from the rows alone, exactly like a licence
   * with no prevention in it.
   *
   * Optional only while the backend that sends it is not deployed everywhere the page runs; the
   * field is not nullable there. Absent, the tile keeps the behaviour it had before this existed
   * (see `availableCatalogues` in the program mapper).
   */
  availableCatalogues?: InterventionTypeDTO[];
}

/** One program the user is in, with where they stand in it */
export interface TodayProgramDTO {
  interventionId: UuidDTO;
  name: string;
  /** Directus asset id of the intervention's image */
  image: UuidDTO;
  /** Value between 0 and 1 */
  progress: number;
  completed: boolean;
  type: InterventionTypeDTO;
}

/** The user's own diary, most recent first. Empty until they write their first entry */
export interface JournalTileDTO {
  entries: JournalEntryDTO[];
}

/** The three things that get a new user started. Null for good once all three are done */
export interface ChecklistTileDTO {
  tourTaken: boolean;
  assessmentAnswered: boolean;
  journalWritten: boolean;
}
